// Ein eindeutiger Name für unser globales Ereignis, um Konflikte zu vermeiden.
const EVENT_LANGUAGE_CHANGE = 'visual-guide:language:change';

class VisualGuideSection extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
    this.isInitialized = false;
  }

  connectedCallback() {
    setTimeout(() => {
      if (!this.isInitialized) {
        this.initVideoPlayers();
        this.initLanguageSwitches();
        this.isInitialized = true;
      }
    }, 100);
    
    document.body.addEventListener(EVENT_LANGUAGE_CHANGE, this.handleLanguageChange);
  }

  disconnectedCallback() {
    document.body.removeEventListener(EVENT_LANGUAGE_CHANGE, this.handleLanguageChange);
  }

  handleLanguageChange(event) {
    const { lang, sectionId } = event.detail;
    if (!sectionId || sectionId === this.sectionId) {
      this.setLanguage(lang);
    }
  }

  setLanguage(targetLang) {
    this.querySelectorAll('[data-lang]').forEach(element => {
      if (element.dataset.lang === targetLang) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    });

    this.querySelectorAll('.language-switch__button').forEach(button => {
      if (button.dataset.switchLang === targetLang) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });
  }

  initLanguageSwitches() {
    this.querySelectorAll('.language-switch__button').forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const targetLang = button.dataset.switchLang;

        document.body.dispatchEvent(new CustomEvent(EVENT_LANGUAGE_CHANGE, {
          bubbles: true,
          detail: { 
            lang: targetLang,
            sectionId: this.sectionId
          }
        }));
      });
    });
  }
  
  initVideoPlayers() {
    this.querySelectorAll('.visual-guide__video-wrapper').forEach(wrapper => {
      const video = wrapper.querySelector('video');
      const overlay = wrapper.querySelector('.nr-video-container__overlay');

      if (!video || !overlay) return;

      // Mobile Detection
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

      // Entferne bereits existierende Listener (falls vorhanden)
      if (wrapper._clickHandler) {
        wrapper.removeEventListener('click', wrapper._clickHandler);
      }
      
      wrapper._clickHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (video.paused) {
          // Versuche auf Mobile Fullscreen zu aktivieren
          if (isMobile) {
            this.playVideoFullscreen(video, overlay);
          } else {
            // Desktop: normales Play
            video.play().catch(console.error);
            overlay.classList.add('hidden');
            video.setAttribute('controls', 'true');
          }
        } else {
          video.pause();
          overlay.classList.remove('hidden');
        }
      };
      
      wrapper.addEventListener('click', wrapper._clickHandler);

      // Event Listeners für Video State
      video.addEventListener('pause', () => {
        if (!video.ended) {
          overlay.classList.remove('hidden');
        }
      });

      video.addEventListener('play', () => {
        overlay.classList.add('hidden');
      });

      // Fullscreen Exit Handler
      video.addEventListener('webkitfullscreenchange', () => {
        if (!document.webkitFullscreenElement) {
          overlay.classList.remove('hidden');
        }
      });

      video.addEventListener('fullscreenchange', () => {
        if (!document.fullscreenElement) {
          overlay.classList.remove('hidden');
        }
      });
    });
  }

  /**
   * Spielt Video im Fullscreen-Modus ab (Mobile optimiert)
   */
  playVideoFullscreen(video, overlay) {
    // Erst Video starten
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
      playPromise.then(() => {
        overlay.classList.add('hidden');
        video.setAttribute('controls', 'true');
        
        // Nach erfolgreichem Start -> Fullscreen aktivieren
        this.enterFullscreen(video);
      }).catch(error => {
        console.error('Video play error:', error);
        // Fallback: Zeige einfach das Video mit Controls
        video.setAttribute('controls', 'true');
        overlay.classList.add('hidden');
      });
    }
  }

  /**
   * Versucht verschiedene Fullscreen-APIs (iOS, Android, Standard)
   */
  enterFullscreen(video) {
    try {
      // iOS Safari: webkitEnterFullscreen
      if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      } 
      // Standard Fullscreen API
      else if (video.requestFullscreen) {
        video.requestFullscreen();
      }
      // Firefox
      else if (video.mozRequestFullScreen) {
        video.mozRequestFullScreen();
      }
      // Chrome/Safari
      else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
      // IE/Edge
      else if (video.msRequestFullscreen) {
        video.msRequestFullscreen();
      }
    } catch (error) {
      console.log('Fullscreen not available:', error);
      // Video läuft trotzdem weiter, nur nicht im Fullscreen
    }
  }
}

// Wird nur einmal ausgeführt, egal wie viele Sektionen du hast
if (!customElements.get('visual-guide-section')) {
  customElements.define('visual-guide-section', VisualGuideSection);
}

// Enhanced Callout functionality with proper image handling
function initCallouts() {
  const calloutHeaders = document.querySelectorAll('[data-callout-toggle]');
  
  calloutHeaders.forEach(header => {
    header.addEventListener('click', function() {
      const container = this.closest('.callout-container');
      const content = container.querySelector('[data-callout-content]');
      const images = content.querySelectorAll('.callout-media');
      
      if (container.classList.contains('is-expanded')) {
        // Collapsing
        content.style.maxHeight = '0px';
        container.classList.remove('is-expanded');
      } else {
        // Expanding
        container.classList.add('is-expanded');
        
        // Calculate height after images are loaded
        if (images.length > 0) {
          let loadedImages = 0;
          const totalImages = images.length;
          
          const calculateHeight = () => {
            requestAnimationFrame(() => {
              const contentHeight = content.scrollHeight;
              content.style.maxHeight = Math.max(contentHeight + 50, 300) + 'px';
            });
          };
          
          // Check if images are already loaded
          images.forEach(img => {
            if (img.complete && img.naturalHeight !== 0) {
              loadedImages++;
            } else {
              img.addEventListener('load', () => {
                loadedImages++;
                if (loadedImages === totalImages) {
                  calculateHeight();
                }
              }, { once: true });
            }
          });
          
          // If all images are already loaded, calculate immediately
          if (loadedImages === totalImages) {
            calculateHeight();
          }
        } else {
          // No images, calculate immediately
          requestAnimationFrame(() => {
            const contentHeight = content.scrollHeight;
            content.style.maxHeight = Math.max(contentHeight + 50, 300) + 'px';
          });
        }
      }
    });
  });
  
  // Also handle dynamic image loading
  const calloutImages = document.querySelectorAll('.callout-media');
  calloutImages.forEach(img => {
    img.addEventListener('load', function() {
      const container = this.closest('.callout-container');
      const content = container?.querySelector('[data-callout-content]');
      
      // Recalculate height if expanded
      if (container?.classList.contains('is-expanded') && content) {
        requestAnimationFrame(() => {
          const contentHeight = content.scrollHeight;
          content.style.maxHeight = Math.max(contentHeight + 50, 300) + 'px';
        });
      }
    });
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initCallouts);
